/** Validated, page-owned URL fields. No browser access is required by codecs. */
export type FieldCodec<V> = {
  param: string;
  def: V;
  enc: (value: V) => string | null;
  /** Params allow a field to normalise against other URL fields. */
  dec: (raw: string, params?: URLSearchParams) => V | undefined;
};
export type Codec<T extends object> = { [K in keyof T]: FieldCodec<T[K]> };

export function encodeState<T extends object>(codec: Codec<T>, state: NoInfer<T>): Record<string, string> {
  const encoded: Record<string, string> = {};
  for (const key of Object.keys(codec) as (keyof T)[]) {
    const field = codec[key];
    const value = field.enc(state[key]);
    if (value !== null && value !== field.enc(field.def)) encoded[field.param] = value;
  }
  return encoded;
}

export function decodeParams<T extends object>(codec: Codec<T>, params: URLSearchParams): T {
  const state = {} as T;
  for (const key of Object.keys(codec) as (keyof T)[]) {
    const field = codec[key];
    const raw = params.get(field.param);
    const decoded = raw === null ? undefined : field.dec(raw, params);
    state[key] = decoded === undefined ? field.def : decoded;
  }
  return state;
}

export function hasOwnParams<T extends object>(codec: Codec<T>, params: URLSearchParams): boolean {
  return (Object.keys(codec) as (keyof T)[]).some(key => params.has(codec[key].param));
}

export function applyToSearch<T extends object>(codec: Codec<T>, current: URLSearchParams, state: NoInfer<T>): URLSearchParams {
  const next = new URLSearchParams(current);
  const encoded = encodeState(codec, state);
  for (const key of Object.keys(codec) as (keyof T)[]) {
    const param = codec[key].param;
    if (encoded[param] === undefined) next.delete(param);
    else next.set(param, encoded[param]);
  }
  return next;
}

export function str(param: string, def = ''): FieldCodec<string> {
  return { param, def, enc: value => value.trim() || null, dec: raw => raw.trim() || undefined };
}
export function oneOf<V extends string>(param: string, allowed: readonly V[], def: V): FieldCodec<V> {
  return { param, def, enc: value => value, dec: raw => allowed.includes(raw as V) ? raw as V : undefined };
}
export function bool(param: string, def = false): FieldCodec<boolean> {
  // '0' is needed for fields whose default is true (Outreach shortlist).
  return { param, def, enc: value => value ? '1' : '0', dec: raw => raw === '1' ? true : raw === '0' ? false : undefined };
}
export function intMin(param: string, def: number, min: number): FieldCodec<number> {
  return { param, def, enc: value => String(value), dec: raw => /^\d+$/.test(raw) && Number.isSafeInteger(Number(raw)) && Number(raw) >= min ? Number(raw) : undefined };
}
export function nullableStr(param: string): FieldCodec<string | null> {
  return { param, def: null, enc: value => value?.trim() || null, dec: raw => raw.trim() || undefined };
}
export function strList<V extends string = string>(param: string, allowed?: readonly V[]): FieldCodec<V[]> {
  return {
    param, def: [], enc: value => value.length ? value.join(',') : null,
    dec: raw => {
      const values = raw.split(',').map(value => value.trim());
      return values.every(value => value && (!allowed || allowed.includes(value as V))) ? [...new Set(values)] as V[] : undefined;
    },
  };
}
/** Date-like fields use the same validation on URL and remembered state. */
export function matching(param: string, pattern: RegExp, def: string | null = null): FieldCodec<string | null> {
  return { param, def, enc: value => value, dec: raw => pattern.test(raw) ? raw : undefined };
}

const PREFIX = 'ssos:nav:';
export type NavState = { search: string; scroll: number };
export function getSessionStorage(): Storage | undefined {
  try { return typeof sessionStorage === 'undefined' ? undefined : sessionStorage; } catch { return undefined; }
}
export function readNav(storage: Storage | undefined, pathname: string): NavState | null {
  try {
    const raw = storage?.getItem(PREFIX + pathname);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (value?.v !== 1 || typeof value.search !== 'string' || !Number.isFinite(value.scroll) || value.scroll < 0) return null;
    return { search: value.search, scroll: value.scroll };
  } catch { return null; }
}
function writeNav(storage: Storage | undefined, pathname: string, patch: Partial<NavState>): void {
  try {
    const previous = readNav(storage, pathname);
    const next = { search: '', scroll: 0, ...previous, ...patch };
    if (previous?.search === next.search && previous.scroll === next.scroll) return;
    storage?.setItem(PREFIX + pathname, JSON.stringify({ v: 1, ...next }));
  } catch { /* Storage is optional (privacy mode / quota). */ }
}
export function writeNavSearch(storage: Storage | undefined, pathname: string, search: string): void {
  writeNav(storage, pathname, { search });
}
export function writeNavScroll(storage: Storage | undefined, pathname: string, scroll: number): void {
  if (Number.isFinite(scroll) && scroll >= 0) writeNav(storage, pathname, { scroll });
}
export function clearNavScroll(storage: Storage | undefined, pathname: string): void {
  if (readNav(storage, pathname)) writeNavScroll(storage, pathname, 0);
}
export function clearNavState(storage: Storage | undefined): void {
  try {
    if (!storage) return;
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key?.startsWith(PREFIX)) storage.removeItem(key);
    }
  } catch { /* Storage is optional. */ }
}

/** Read-only resolution also makes StrictMode's repeated initialisation safe. */
export function resolveViewState<T extends object>(codec: Codec<T>, search: string, remembered: NavState | null, navigationType: 'POP' | 'PUSH' | 'REPLACE' = 'PUSH'): { initial: T; clearScroll: boolean } {
  const params = new URLSearchParams(search);
  // Every non-default view owns URL params, so a bare history entry means defaults.
  const explicit = hasOwnParams(codec, params) || navigationType === 'POP';
  const initial = decodeParams(codec, explicit ? params : new URLSearchParams(remembered?.search));
  const canonical = (state: T) => new URLSearchParams(encodeState(codec, state)).toString();
  return {
    initial,
    clearScroll: explicit && canonical(initial) !== canonical(decodeParams(codec, new URLSearchParams(remembered?.search))),
  };
}

/** Compare only owned params, retaining explicit defaults (which override memory). */
export function canonicalSearch<T extends object>(codec: Codec<T>, search: string): string {
  const params = new URLSearchParams(search);
  const own = new URLSearchParams();
  for (const key of Object.keys(codec) as (keyof T)[]) {
    const param = codec[key].param;
    if (params.has(param)) own.set(param, params.get(param)!);
  }
  own.sort();
  return own.toString();
}

export function shouldRestore<T extends object>(lastSearch: string, search: string, codec: Codec<T>): boolean {
  return lastSearch !== canonicalSearch(codec, search);
}
